import React, { useState } from 'react';
import { ExclamationIcon } from '@heroicons/react/outline';

/**
 * ProfileCompletionBanner
 *
 * fields: array of { label: string, filled: boolean }
 * onEdit: optional callback — called when user clicks "Update Now"
 *         If not provided, the button is omitted
 */
const ProfileCompletionBanner = ({ fields = [], onEdit }) => {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  const missing = fields.filter(f => !f.filled);
  const total = fields.length;
  const filled = total - missing.length;
  const pct = total > 0 ? Math.round((filled / total) * 100) : 100;

  if (missing.length === 0) return null;

  return (
    <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1">
          <ExclamationIcon className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-800">
              Profile {pct}% complete — {missing.length} field{missing.length !== 1 ? 's' : ''} missing
            </p>
            <p className="text-xs text-amber-700 mt-0.5 mb-3">
              A complete profile helps organizers trust you and speeds up event applications.
            </p>

            {/* Progress bar */}
            <div className="w-full h-1.5 bg-amber-200 rounded-full overflow-hidden mb-3">
              <div
                className="h-full bg-amber-500 rounded-full transition-all duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>

            {/* Missing fields */}
            <div className="flex flex-wrap gap-2">
              {missing.map(f => (
                <span
                  key={f.label}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded-full"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                  {f.label}
                </span>
              ))}
            </div>

            {onEdit && (
              <button
                onClick={onEdit}
                className="mt-3 text-xs font-semibold text-amber-800 underline underline-offset-2 hover:text-amber-900"
              >
                Update now →
              </button>
            )}
          </div>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="text-amber-400 hover:text-amber-600 shrink-0 mt-0.5 text-lg leading-none"
          aria-label="Dismiss"
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default ProfileCompletionBanner;
